import React, { useState } from 'react';
import { CheckCircle, Info, Zap, ShieldAlert, Sparkles, ArrowRight, Bot, Newspaper, Loader2, Ban, Skull } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../api/api';

export interface AiInsight {
  type: 'SUCCESS' | 'WARNING' | 'DANGER' | 'INFO';
  titleKey: string;
  messageKey: string;
  messageArgs?: any;
  metric?: string;
  actionKey?: string;
}

interface AiAdvisorProps {
  insight: AiInsight;
}

export const AiAdvisor: React.FC<AiAdvisorProps> = ({ insight }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  if (!insight) return null;

  const getDesign = () => {
    switch (insight.type) {
      case 'DANGER':
        return {
          container: 'bg-red-500/10 border-red-500/20 shadow-[0_0_30px_-10px_rgba(239,68,68,0.2)]',
          iconColor: 'text-red-400',
          iconBg: 'bg-red-500/20',
          icon: <ShieldAlert size={20} className="animate-pulse" />,
          button: 'hover:bg-red-500 hover:text-white border-red-500/30'
        };
      case 'WARNING':
        return {
          container: 'bg-orange-500/10 border-orange-500/20 shadow-[0_0_30px_-10px_rgba(249,115,22,0.2)]',
          iconColor: 'text-orange-400',
          iconBg: 'bg-orange-500/20',
          icon: <Zap size={20} className="animate-bounce" style={{ animationDuration: '3s' }} />,
          button: 'hover:bg-orange-500 hover:text-white border-orange-500/30'
        };
      case 'SUCCESS':
        return {
          container: 'bg-emerald-500/10 border-emerald-500/20 shadow-[0_0_30px_-10px_rgba(16,185,129,0.2)]',
          iconColor: 'text-emerald-400',
          iconBg: 'bg-emerald-500/20',
          icon: <CheckCircle size={20} />,
          button: 'hover:bg-emerald-500 hover:text-white border-emerald-500/30'
        };
      default:
        const isNews = insight.metric === 'Gemini Live' || insight.titleKey.includes('news');
        return {
          container: 'bg-blue-500/10 border-blue-500/20 shadow-[0_0_30px_-10px_rgba(59,130,246,0.2)]',
          iconColor: 'text-blue-400',
          iconBg: 'bg-blue-500/20',
          icon: isNews ? <Newspaper size={20} /> : <Info size={20} />,
          button: 'hover:bg-blue-500 hover:text-white border-blue-500/30'
        };
    }
  };

  const handleAction = async () => {
    if (!insight.actionKey) return;
    if (loading) return;
    setLoading(true);

    try {
        // 1. БЛОКИРОВКА ТОКСИЧНОЙ МОНЕТЫ
        if (insight.actionKey === 'ai.toxic.action') {
            await api.post(`/users/blacklist`, { 
              symbol: insight.messageArgs?.coin || 'UNKNOWN',
              hours: 24 
            });
            alert(t('ai.actions.block_success', { coin: insight.messageArgs?.coin }));
        }
        
        // 2. ПАУЗА БОТА (ТИЛТ)
        else if (insight.actionKey === 'ai.tilt.action') {
             await api.post(`/trading/toggle`, { status: false });
             alert(t('ai.actions.pause_success'));
        }

        // 3. ЗАКРЫТЬ УБЫТКИ (PANIC SELL) ✅ ДОБАВЛЕНО
        else if (insight.actionKey === 'ai.hold.action') {
             const res = await api.post(`/trading/panic`);
             alert(`📉 Panic Sell executed. Closed ${res.data.closed} trades.`);
        }

    } catch (e) {
      console.error("Action error:", e);
      alert(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const design = getDesign();

  // Выбор иконки для кнопки
  const getActionIcon = () => {
      if (insight.actionKey === 'ai.toxic.action') return <Ban size={12} />;
      if (insight.actionKey === 'ai.hold.action') return <Skull size={12} />;
      return <ArrowRight size={12} />;
  };

  return (
    <div className={`group relative overflow-hidden rounded-[24px] border backdrop-blur-xl ${design.container} p-5 transition-all duration-500 h-full flex flex-col hover:scale-[1.01]`}>
      
      <div className={`absolute -inset-24 bg-gradient-to-br ${insight.type === 'DANGER' ? 'from-red-500/5' : 'from-blue-500/5'} to-transparent blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700`}></div>

      <div className="relative z-10 flex items-center gap-2 mb-3 flex-shrink-0">
        <div className={`p-1.5 rounded-lg ${design.iconBg}`}>
            <Bot size={14} className={design.iconColor} />
        </div>
        <span className={`text-[10px] font-black uppercase tracking-[0.15em] ${design.iconColor}`}>
            {t('ai.advisor_label') as string}
        </span>
        {insight.metric && (
            <span className="ml-auto text-[10px] bg-slate-900/90 px-2 py-0.5 rounded-[6px] text-slate-300 font-mono border border-white/5 shadow-lg whitespace-nowrap">
                {insight.metric}
            </span>
        )}
      </div>

      <div className="relative z-10 flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
          <h3 className="text-white font-bold text-sm mb-2 flex items-center gap-2">
             {design.icon}
             {t(insight.titleKey) as string}
          </h3>
          <p className="text-slate-300 text-xs leading-relaxed font-medium bg-slate-950/40 p-3 rounded-xl border border-white/5 italic shadow-inner">
            "{t(insight.messageKey, insight.messageArgs) as string}"
          </p>
      </div>

      {insight.actionKey && (
        <button 
          onClick={handleAction}
          disabled={loading}
          className={`relative z-10 mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900/80 text-white text-[10px] font-bold uppercase tracking-widest transition-all border border-slate-700 shadow-xl ${design.button} ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {loading ? (
             <Loader2 size={12} className="animate-spin" />
          ) : (
             <>
               {getActionIcon()}
               {t(insight.actionKey) as string}
             </>
          )}
        </button>
      )}

      <div className="absolute top-2 right-2 opacity-10 group-hover:opacity-30 transition-opacity duration-700 pointer-events-none">
         <Sparkles className={`${design.iconColor} animate-pulse`} size={40} />
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 3px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
      `}</style>
    </div>
  );
};